import { api } from '../lib/api';

type ClientErrorSource = 'error-boundary' | 'window-error' | 'unhandled-rejection';

type ClientErrorReportInput = {
  source: ClientErrorSource;
  name?: string | null;
  message: string;
  stack?: string | null;
  componentStack?: string | null;
  metadata?: Record<string, unknown>;
};

const recentReports = new Set<string>();

function currentUrl() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.location.href;
}

function userAgent() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.navigator.userAgent;
}

function reportKey(input: ClientErrorReportInput) {
  return [
    input.source,
    input.name ?? '',
    input.message,
    input.stack?.slice(0, 500) ?? '',
    input.componentStack?.slice(0, 500) ?? '',
  ].join('|');
}

export function describeUnknownError(error: unknown) {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack ?? null,
    };
  }

  if (typeof error === 'string') {
    return {
      name: null,
      message: error,
      stack: null,
    };
  }

  return {
    name: null,
    message: 'Unknown client error',
    stack: (() => {
      try {
        return JSON.stringify(error);
      } catch {
        return String(error);
      }
    })(),
  };
}

export async function reportClientError(input: ClientErrorReportInput) {
  const key = reportKey(input);
  if (recentReports.has(key)) {
    return;
  }

  recentReports.add(key);
  window.setTimeout(() => recentReports.delete(key), 30_000);

  try {
    await api.post('/support/client-errors', {
      ...input,
      currentUrl: currentUrl(),
      userAgent: userAgent(),
      appVersion: import.meta.env.VITE_APP_VERSION ?? null,
    });
  } catch (reportError) {
    console.error('[Client error report failed]', reportError);
  }
}
